import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
  Form,
  Input,
  Modal,
  Spin,
  Select,
  Button,
  Popconfirm,
  Tooltip,
  Upload,
  UploadProps,
  message,
  Switch,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import componentContent from './PushEditModal.content.json';
import useTranslate from '@hooks/useTranslate';
import { useStore } from 'effector-react';
import css from './PushEditModal.module.less';
import { useForm } from 'antd/lib/form/Form';
import modals, { closeModal, IModalsState } from '@stores/modals';
import IPush, { ICreatePush } from '@interfaces/IPush';
import ExclamationCircleFilled from '@ant-design/icons/lib/icons/ExclamationCircleFilled';
import push, { createPush, getSurveyTopic, getSurveyText, getSurveyButton } from '@stores/push';

const { confirm } = Modal;

const FormItem = Form.Item;
const { Option } = Select;

const onFinish = (values: Partial<ICreatePush>, fileCSV: Blob) => {
  const data = new FormData();
  data.append('file', fileCSV);
  data.append('name', String(values.name));
  data.append('textKey', String(values.textKey));
  if (values.sendActivity) {
    data.append('sendActivity', 'true');
    data.append('buttonKey', String(values.buttonKey));
  }
  data.append('topicKey', String(values.topicKey));
  if (values.deeplink) data.append('deeplink', String(values.deeplink));
  else data.append('externalLink', String(values.externalLink));

  createPush(data).then(() => closeModal({ name: 'push' }));
};

const mapDataToFieldsValue = (data?: IPush) => {
  return {
    name: data?.name,
  };
};

const PushEditModal: FunctionComponent<unknown> = () => {
  const content = useTranslate(componentContent);
  const {
    visibleState: { push: visible },
    data,
  } = useStore<IModalsState<IPush>>(modals);
  const { preloaders, preloadersGetSurvey, listSurveyTopic, listSurveyText, listSurveyButton } =
    useStore(push);
  const [form] = useForm();

  const [fileCSV, setFileCSV] = useState<Blob>();
  const [isDisableSubmit, setIsDisableSubmit] = useState(false);
  const [allFieldsFilled, setAllFieldsFilled] = useState(false);
  const [linkBoth, setLinkBoth] = useState(false);
  const [sendActivity, setSendActivity] = useState(false);

  const props: UploadProps = {
    maxCount: 1,
    customRequest: ({ file, onSuccess }) => {
      setFileCSV(() => {
        return file as Blob;
      });
      setTimeout(() => onSuccess && onSuccess({ statusText: 'ok' }), 0);
    },
    beforeUpload: (file) => {
      const isCSV = file.type === 'text/csv' || file.type === 'csv';
      if (!isCSV) {
        message.error(`${file.name} is not a csv file`);
      }
      return isCSV || Upload.LIST_IGNORE;
    },
  };

  useEffect(() => {
    getSurveyTopic();
    getSurveyText();
    getSurveyButton();
    return () => {
      push.reset();
    };
  }, []);

  useEffect(() => {
    if (visible && data) {
      const fieldsValue = mapDataToFieldsValue(data);
      form.setFieldsValue(fieldsValue);
      setIsDisableSubmit(false);
      setAllFieldsFilled(false);
      setLinkBoth(false);
    } else {
      form.resetFields();
      form.setFieldsValue(mapDataToFieldsValue());
    }
  }, [visible, data, form, listSurveyTopic]);

  const checkBeforeSubmit = useCallback(() => {
    if (
      !form.getFieldValue('name') ||
      !form.getFieldValue('file') ||
      !form.getFieldValue('textKey') ||
      !form.getFieldValue('topicKey') ||
      !(form.getFieldValue('deeplink') || form.getFieldValue('externalLink'))
    ) {
      setAllFieldsFilled(true);
    } else if (form.getFieldValue('deeplink') && form.getFieldValue('externalLink')) {
      setLinkBoth(true);
    } else {
      form.submit();
    }
  }, [data, form]);

  const onClose = (
    description: string,
    title: string,
    no: string,
    yes: string,
    onConfirm?: any,
  ) => {
    confirm({
      title: title,
      icon: <ExclamationCircleFilled />,
      content: description,
      okText: yes,
      cancelText: no,
      onOk() {
        if (onConfirm) onConfirm();
      },
      onCancel() {
        closeModal({ name: 'push' });
      },
    });
  };

  if (!visible) return null;
  return (
    <Form
      onFinish={(values) => onFinish(values, fileCSV as Blob)}
      form={form}
      labelAlign='left'
      labelWrap
      labelCol={{ flex: '180px' }}
    >
      <Modal
        title={content.title}
        confirmLoading={!!preloaders.createPush && !!preloadersGetSurvey}
        visible
        onCancel={() =>
          onClose(content.confirm_description, content.confirm_title, content.no, content.yes)
        }
        width={800}
        footer={[
          <Button
            key='cancel'
            onClick={() =>
              onClose(content.confirm_description, content.confirm_title, content.no, content.yes)
            }
          >
            {content.cancel}
          </Button>,
          <Tooltip
            key='submit'
            title={isDisableSubmit ? content.error_send : ''}
          >
            <Button
              htmlType='submit'
              type='primary'
              onClick={checkBeforeSubmit}
              disabled={isDisableSubmit}
            >
              {content.save}
            </Button>
          </Tooltip>,
          <Popconfirm
            key='confirmSubmit'
            visible={allFieldsFilled || linkBoth}
            title={linkBoth ? content.error_both_link : content.error_send}
            showCancel={false}
            onConfirm={() => {
              setAllFieldsFilled(false);
              setLinkBoth(false);
            }}
          ></Popconfirm>,
        ]}
      >
        <Spin spinning={!!preloaders.createPush && !!preloadersGetSurvey}>
          <FormItem
            label={content.name}
            name='name'
          >
            <Input />
          </FormItem>

          <FormItem
            className={css.upload}
            name='file'
          >
            <Upload
              className={css.upload}
              {...props}
            >
              <Button
                className={css.addButton}
                icon={<UploadOutlined />}
              >
                {content.upload}
              </Button>
            </Upload>
          </FormItem>

          <FormItem
            label={content.text}
            name='textKey'
          >
            <Select style={{ width: '100%' }}>
              {listSurveyText.map((item) => (
                <Option
                  value={item.key}
                  key={item.key}
                >
                  {item.text}
                </Option>
              ))}
            </Select>
          </FormItem>

          <FormItem
            label={content.topic}
            name='topicKey'
          >
            <Select style={{ width: '100%' }}>
              {listSurveyTopic.map((item) => (
                <Option
                  value={item.key}
                  key={item.key}
                >
                  {item.text}
                </Option>
              ))}
            </Select>
          </FormItem>

          <FormItem
            label={content.deeplink}
            name='deeplink'
          >
            <Input />
          </FormItem>

          <FormItem
            label={content.externalLink}
            name='externalLink'
          >
            <Input />
          </FormItem>

          <FormItem
            label={content.notification_duplicate}
            name='sendActivity'
            valuePropName='checked'
          >
            <Switch onChange={setSendActivity} />
          </FormItem>

          <FormItem
            label={content.notification_key}
            name='buttonKey'
          >
            <Select
              style={{ width: '100%' }}
              disabled={!sendActivity}
            >
              {listSurveyButton.map((item) => (
                <Option
                  value={item.key}
                  key={item.key}
                >
                  {item.text}
                </Option>
              ))}
            </Select>
          </FormItem>
        </Spin>
      </Modal>
    </Form>
  );
};

export default PushEditModal;
